import React from 'react';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Grid, Grow, Paper, Typography } from '@mui/material';
import Loading from '@/components/loading';

import { IdentifiedWeaknessCategory } from '.';

interface ListIdentifiedWeaknessesStatsProps {
  categories: IdentifiedWeaknessCategory[] | null;
  loading: boolean;
}

const BAR_COLOR = '#4CAF7D';
const TOTAL_COLOR = '#1A3B5D';

type WaterfallEntry = {
  category: string;
  offset: number;
  value: number;
  proportion: number;
  totalEvents: number;
  isTotal: boolean;
};

function renderBarLabel(waterfallEntries: WaterfallEntry[]) {
  return (props: any) => {
    const { x, y, width, index } = props;
    const entry = waterfallEntries[index];
    if (!entry) return null;
    const label = `${entry.proportion.toFixed(1)}%`;
    const fill = entry.isTotal ? '#fff' : '#1A3B5D';
    return (
      <text
        dominantBaseline="middle"
        fill={fill}
        fontSize={11}
        fontWeight="bold"
        textAnchor="middle"
        x={x + width / 2}
        y={y + 14}
      >
        {label}
      </text>
    );
  };
}

function buildWaterfallData(
  categories: IdentifiedWeaknessCategory[]
): WaterfallEntry[] {
  const sorted = [...categories]
    .filter((c) => c.proportion > 0)
    .sort((a, b) => a.proportion - b.proportion);
  const total = sorted.reduce((sum, c) => sum + c.proportion, 0);
  const totalEvents = sorted.reduce((sum, c) => sum + (c.totalEvents ?? 0), 0);

  let cumulative = 0;
  const entries: WaterfallEntry[] = sorted.map((c) => {
    const entry: WaterfallEntry = {
      category: c.category,
      offset: cumulative,
      value: c.proportion,
      proportion: c.proportion,
      totalEvents: c.totalEvents ?? 0,
      isTotal: false,
    };
    cumulative += c.proportion;
    return entry;
  });

  entries.push({
    category: 'Total',
    offset: 0,
    value: total,
    proportion: total,
    totalEvents,
    isTotal: true,
  });

  return entries;
}

const formatPercentage = (value: number | string) => {
  const number = Number(value);
  if (Number.isNaN(number)) return '-';
  return `${number.toFixed(2)}%`;
};

const CustomTooltip = (props: any) => {
  if (props.active && props.payload && props.payload.length) {
    const entry: WaterfallEntry = props.payload[0].payload;
    return (
      <Box>
        <Paper sx={{ p: 1 }}>
          <Typography variant="subtitle2">{entry.category}</Typography>
          <Typography>
            {entry.isTotal ? 'Total' : 'Proporção'}:{' '}
            {formatPercentage(entry.proportion)}
          </Typography>
          <Typography>Total de eventos: {entry.totalEvents}</Typography>
        </Paper>
      </Box>
    );
  }
  return null;
};

export const ListIdentifiedWeaknessesStats: React.FC<
  ListIdentifiedWeaknessesStatsProps
> = ({ loading, categories: rawCategories = [] }) => {
  const categories = rawCategories ?? [];
  const shouldRenderChart = categories.length > 0 && !loading;
  const waterfallData = shouldRenderChart ? buildWaterfallData(categories) : [];
  const barWidth = Math.max(
    40,
    Math.min(60, 600 / (waterfallData.length || 1))
  );

  return (
    <Grow in unmountOnExit>
      <Grid
        container
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(480px, auto))',
        }}
        sx={{ mt: 2, gap: 2, width: '100%', ml: 0 }}
      >
        <Loading backgroundColor="transparent" loading={loading} />
        {shouldRenderChart && (
          <Paper sx={{ p: 2, width: '100%', gridColumn: '1/-1' }}>
            <Typography sx={{ mb: 2, fontWeight: 'bold' }} variant="h6">
              Categorias de Fraquezas Identificadas
            </Typography>
            <ResponsiveContainer height={360} width="100%">
              <BarChart
                barSize={barWidth}
                data={waterfallData}
                margin={{ top: 20, right: 30, left: 10, bottom: 80 }}
              >
                <CartesianGrid stroke="#f5f5f5" vertical={false} />
                <XAxis
                  angle={-35}
                  dataKey="category"
                  interval={0}
                  textAnchor="end"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                />
                <YAxis tickFormatter={(v) => `${v.toFixed(0)}%`} />
                <Tooltip content={<CustomTooltip />} />
                {/* Transparent offset bar (base of waterfall) */}
                <Bar dataKey="offset" fill="transparent" stackId="waterfall">
                  {waterfallData.map((entry) => (
                    <Cell
                      fill="transparent"
                      key={`offset-${entry.category}`}
                      stroke="transparent"
                    />
                  ))}
                </Bar>
                {/* Visible value bar */}
                <Bar dataKey="value" name="Proporção" stackId="waterfall">
                  <LabelList
                    content={renderBarLabel(waterfallData)}
                    dataKey="proportion"
                    position="top"
                  />
                  {waterfallData.map((entry) => (
                    <Cell
                      fill={entry.isTotal ? TOTAL_COLOR : BAR_COLOR}
                      key={`bar-${entry.category}`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {categories.length === 0 && !loading && (
              <span>
                Nenhum resultado para mostrar com o filtro selecionado.
              </span>
            )}
          </Paper>
        )}
      </Grid>
    </Grow>
  );
};
